import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ErrorHandlerService } from '@core/services/error-handler/error-handler.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiService } from '@shared/services/api/api.service';
import { NgxNotifyService } from '@shared/services/ngx-notify/ngx-notify.service';
import { EMPTY, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { ProtocolService } from '../services/protocol.service';
import { protocolLoadError } from '../services/protocol-load-error';

import { ProtocolListModel } from '../models/protocol-list.model';
import { ProtocolModel } from '../models/protocol.model';
import * as protocolActions from './protocol.actions';

@Injectable()
export class ProtocolEffects {

    protocolSearch$ = createEffect(() =>
        this.actions$.pipe(
            ofType(protocolActions.protocolSearch),
            switchMap((actions) =>
                this.apiService.search<ProtocolListModel>(this.endpoint, actions.options).pipe(
                    map(res =>
                        protocolActions.protocolSearchLoaded({
                            protocols: res.protocols,
                            count: res.count
                        }),
                        catchError((err) => {
                            this.notify.error(this.errorHandler.handleError(err));
                            return EMPTY;
                        })
                    )
                ),
            )
        )
    );

    protocolById$ = createEffect(() =>
        this.actions$.pipe(
            ofType(protocolActions.protocolById),
            switchMap((actions) =>
                this.protocolService.getById(actions.id).pipe(
                    map(res => {
                        if (res.id !== actions.id) throw new Error('Protocol response does not match the requested ID');
                        return protocolActions.protocolByIdLoaded({ data: { protocol: res } });
                    }),
                    catchError((err: unknown) => {
                        const error = protocolLoadError(err);
                        this.notify.error(error);
                        return of(protocolActions.protocolByIdFailed({ id: actions.id, error }));
                    })
                ),
            )
        ),
    );

    protocolHistoryItem$ = createEffect(() =>
        this.actions$.pipe(
            ofType(protocolActions.protocolHistoryItem),
            switchMap(({ protocolId, historyId }) =>
                this.protocolService.getHistoryItem(protocolId, historyId).pipe(
                    map(history => {
                        if (history.id !== protocolId || history.protocolHistoryId !== historyId) {
                            throw new Error('History response does not match the requested snapshot');
                        }
                        return protocolActions.protocolHistoryItemLoaded({ protocolId, historyId, history });
                    }),
                    catchError((err: unknown) => {
                        const error = protocolLoadError(err);
                        this.notify.error(error);
                        return of(protocolActions.protocolHistoryItemFailed({ protocolId, historyId, error }));
                    })
                )
            )
        )
    );

    protocolCreate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(protocolActions.protocolCreate),
            switchMap((actions) =>
                this.apiService.create<ProtocolModel>(this.endpoint, actions.data.protocol).pipe(
                    map(res => {
                        this.notify.success(`Created ${res.studyTitle}`);
                        this.router.navigate(['/protocol', res.id]);
                        return protocolActions.protocolCreateLoaded({ data: { protocol: res } });
                    }),
                    catchError((err) => {
                        this.notify.error(this.errorHandler.handleError(err));
                        return EMPTY;
                    })
                ),
            )
        ),
    );

    protocolUpdate$ = createEffect(() =>
        this.actions$.pipe(
            ofType(protocolActions.protocolUpdate),
            switchMap((actions) =>
                this.apiService.update<ProtocolModel>(this.endpoint, actions.data.protocol).pipe(
                    map(res => {
                        this.notify.success(`Updated ${res.studyTitle}`);
                        return protocolActions.protocolUpdateLoaded({ data: { protocol: res } });
                    }),
                    catchError((err) => {
                        this.notify.error(this.errorHandler.handleError(err));
                        return EMPTY;
                    })
                ),
            )
        ),
    );

    private endpoint = 'protocol';

    constructor(
        private actions$: Actions,
        private notify: NgxNotifyService,
        private apiService: ApiService,
        private errorHandler: ErrorHandlerService,
        private protocolService: ProtocolService,
        private router: Router) { }
}
